import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { MessageSquare, User, Clock, Phone, ChevronRight, RefreshCw, Pause, Play, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useTheme } from '../../context/ThemeContext';

const API = process.env.REACT_APP_BACKEND_URL;
const deliveryLabel = (status) => ({
  pending: 'Pendiente de envío', submitting: 'En proceso',
  accepted: 'Aceptado por WhatsApp; entrega pendiente',
  sent: 'En WhatsApp; entrega pendiente', delivered: 'Entregado', read: 'Leído',
  failed: 'Entrega fallida', unknown: 'Envío sin confirmar',
}[status] || 'Entrega no confirmada');

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [listError, setListError] = useState('');
  const [queryAlerts, setQueryAlerts] = useState(null);
  const [messageError, setMessageError] = useState('');
  const listRequest = useRef(0);
  const messageRequest = useRef(0);
  const messagesRef = useRef([]);
  const messageBusy = useRef(false);
  const selection = useRef(null);
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchQuery.trim()); }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const { theme } = useTheme();

  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const inputBg = theme === 'dark' ? 'bg-slate-900 border-slate-600 text-white placeholder:text-gray-400' : 'bg-white border-gray-300 text-gray-900';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';
  const borderColor = theme === 'dark' ? 'border-slate-700' : 'border-gray-200';
  const dividerColor = theme === 'dark' ? 'divide-slate-700' : 'divide-gray-200';
  const hoverBg = theme === 'dark' ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50';
  const selectedBg = theme === 'dark' ? 'bg-emerald-500/10' : 'bg-emerald-50';
  const messageBgPatient = theme === 'dark' ? 'bg-slate-700/50 text-white' : 'bg-gray-100 text-gray-900';

  // Get credentials with consistent fallback
  const getCredentials = () => {
    let clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    if (!clinicId) {
      clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';
      localStorage.setItem('clinicId', clinicId);
    }
    return { clinicId, token };
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { clinicId, token } = getCredentials();
      try {
        const { data } = await axios.get(`${API}/api/clinic/${clinicId}/owner-query-alerts`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (active) setQueryAlerts(data);
      } catch (_) { if (active) setQueryAlerts(null); }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const fetchConversations = useCallback(async () => {
    const requestId = ++listRequest.current;
    const { clinicId, token } = getCredentials();
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/clinic/${clinicId}/conversations`, {
        params: { paginated: true, offset: 0, limit: 50, search },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (requestId !== listRequest.current) return;
      setConversations(response.data.items || []);
      setTotal(response.data.total || 0);
      setListError('');
    } catch (error) {
      console.error('Error fetching conversations:', error);
      if (requestId === listRequest.current) setListError('No se pudo cargar el historial. Intenta actualizar.');
    } finally {
      if (requestId === listRequest.current) setLoading(false);
    }
  }, [search]);

  const fetchMessages = useCallback(async (conversationId, { older = false, quiet = false } = {}) => {
    if ((quiet || older) && messageBusy.current) return;
    const requestId = ++messageRequest.current;
    messageBusy.current = true;
    const offset = older ? messagesRef.current.length : 0;
    const { clinicId, token } = getCredentials();
    try {
      if (older) setLoadingOlder(true);
      else if (!quiet) setLoadingMessages(messagesRef.current.length === 0);
      const response = await axios.get(`${API}/api/clinic/${clinicId}/conversations/${conversationId}/messages`, {
        params: { paginated: true, offset, limit: 50, mark_read: false },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (requestId !== messageRequest.current || selection.current !== conversationId) return;
      const byId = new Map(messagesRef.current.map(msg => [msg.id, msg]));
      let page = response.data;
      const received = [...(page.items || [])];
      // Catch up across a burst of more than one page before joining the history.
      let nextOffset = received.length;
      while (!older && byId.size && page.has_more && page.items?.length
        && !received.some(msg => byId.has(msg.id))) {
        const next = await axios.get(`${API}/api/clinic/${clinicId}/conversations/${conversationId}/messages`, {
          params: { paginated: true, offset: nextOffset, limit: 50, mark_read: false },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (requestId !== messageRequest.current || selection.current !== conversationId) return;
        page = next.data;
        received.push(...(page.items || []));
        nextOffset += (page.items || []).length;
      }
      for (const msg of received) byId.set(msg.id, msg);
      const merged = [...byId.values()].sort((a, b) =>
        (a.created_at || '').localeCompare(b.created_at || '') || String(a.id).localeCompare(String(b.id)));
      if (older || messagesRef.current.length === 0) setHasMoreMessages(Boolean(response.data.has_more));
      messagesRef.current = merged;
      setMessages(merged);
      setMessageError('');
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (requestId === messageRequest.current) setMessageError('No se pudieron cargar los mensajes. Vuelve a seleccionar la conversación.');
    } finally {
      if (requestId === messageRequest.current) {
        messageBusy.current = false;
        setLoadingMessages(false);
        setLoadingOlder(false);
      }
    }
  }, []);

  const selectedId = selectedConversation?.conversation_id;
  useEffect(() => {
    if (!selectedId) return undefined;
    const interval = setInterval(() => fetchMessages(selectedId, { quiet: true }), 15000);
    return () => clearInterval(interval);
  }, [selectedId, fetchMessages]);

  useEffect(() => () => { messageRequest.current += 1; messageBusy.current = false; }, []);

  useEffect(() => {
    fetchConversations();
    // Refresh every 30 seconds
    const interval = !search ? setInterval(fetchConversations, 30000) : null;
    return () => { clearInterval(interval); listRequest.current += 1; };
  }, [fetchConversations, search]);

  const selectConversation = async (conv) => {
    selection.current = conv.conversation_id;
    messagesRef.current = [];
    setMessages([]);
    setHasMoreMessages(false);
    setLoadingOlder(false);
    setManualMessage('');
    setMessageError('');
    setIsPaused(false);
    setSelectedConversation(conv);
    fetchMessages(conv.conversation_id);
    // Check takeover status
    const { clinicId, token } = getCredentials();
    try {
      const response = await axios.get(`${API}/api/clinic/${clinicId}/takeover-status/${conv.phone}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (selection.current === conv.conversation_id) setIsPaused(response.data.is_paused);
    } catch (error) {
      console.error('Error checking takeover status:', error);
      if (selection.current === conv.conversation_id) setIsPaused(false);
    }
  };

  const toggleTakeover = async () => {
    if (!selectedConversation) return;
    const { clinicId, token } = getCredentials();
    const phone = selectedConversation.phone;
    
    try {
      if (isPaused) {
        // Reactivate bot
        await axios.delete(`${API}/api/clinic/${clinicId}/takeover/${phone}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setIsPaused(false);
        alert('✅ Bot reactivado para esta conversación');
      } else {
        // Pause bot
        await axios.post(`${API}/api/clinic/${clinicId}/takeover/${phone}`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setIsPaused(true);
        alert('⏸️ Bot pausado. Puedes responder manualmente.');
      }
    } catch (error) {
      console.error('Error toggling takeover:', error);
      alert('Error al cambiar el estado del bot');
    }
  };

  const sendManualMessage = async () => {
    if (!selectedConversation || !manualMessage.trim()) return;
    const { clinicId, token } = getCredentials();
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/api/clinic/${clinicId}/send-manual-message?phone=${encodeURIComponent(selectedConversation.phone)}&message=${encodeURIComponent(manualMessage)}`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setManualMessage('');
      // Refresh messages
      fetchMessages(selectedConversation.conversation_id);
      alert('✅ Mensaje enviado');
    } catch (error) {
      console.error('Error sending manual message:', error);
      alert('Error al enviar mensaje: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingMessage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} hrs`;
    return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  // Search runs on the server across the full history, not this page only.
  const filteredConversations = conversations;

  return (
    <DashboardLayout title="Conversaciones">
      {queryAlerts?.enabled && (
        <p className={`${textSecondary} text-sm mb-3`} role="status">
          Copias y avisos a {queryAlerts.destination?.slice(-10)} · {queryAlerts.recent?.length ? `Último aviso: ${deliveryLabel(queryAlerts.recent[0].status)}` : 'Sin avisos registrados; tus propias pruebas no generan copias.'}
          {queryAlerts.blocker && ' · Envío de avisos bloqueado; requiere revisar la conexión de WhatsApp.'}
        </p>
      )}
      {queryAlerts?.social_monitor && (
        <p className={`${textSecondary} text-sm mb-3`} role="status">
          Revisión de redes cada hora · Respuestas sólo con tu autorización.
          {queryAlerts.social_monitor.networks?.youtube?.status === 'not_configured' && ' YouTube: conexión pendiente.'}
          {queryAlerts.social_monitor.networks?.tiktok?.status === 'not_connected' && ' TikTok: conexión pendiente.'}
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:h-[calc(100vh-200px)]">
        {/* Conversations List */}
        <Card className={`${cardBg} overflow-hidden flex flex-col h-[400px] lg:h-auto min-h-0`}>
          <CardHeader className={`border-b ${borderColor} flex flex-row items-center justify-between`}>
            <CardTitle className={`${textPrimary} flex items-center gap-2`}>
              <MessageSquare className="w-5 h-5" />
              Conversaciones ({total})
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchConversations}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <div className={`p-3 border-b ${borderColor}`}>
            <Input
              placeholder="Buscar por nombre, teléfono o mensaje..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`${inputBg} w-full`}
            />
          </div>
          {listError && <p role="alert" className="p-3 text-red-500">{listError}</p>}
          <CardContent className="p-0 overflow-y-auto flex-1 min-h-0">
            {loading && filteredConversations.length === 0 ? (
              <div className={`p-8 text-center ${textSecondary}`}>Cargando...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className={`w-12 h-12 ${textMuted} mx-auto mb-4`} />
                <p className={textSecondary}>
                  {searchQuery ? 'No se encontraron conversaciones' : 'No hay conversaciones aún'}
                </p>
                <p className={`${textMuted} text-sm mt-1`}>
                  {searchQuery ? 'Intenta con otro término de búsqueda' : 'Los mensajes de WhatsApp aparecerán aquí'}
                </p>
              </div>
            ) : (
              <div className={`divide-y ${dividerColor}`}>
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 cursor-pointer ${hoverBg} transition-colors ${
                      selectedConversation?.conversation_id === conv.conversation_id ? `${selectedBg} border-l-2 border-emerald-500` : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div>
                          <p className={`${textPrimary} font-medium`}>{conv.patient_name || 'Paciente'}</p>
                          <p className={`${textSecondary} text-sm flex items-center gap-1`}>
                            <Phone className="w-3 h-3" />
                            {conv.phone}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`${textMuted} text-xs flex items-center gap-1`}>
                          <Clock className="w-3 h-3" />
                          {formatDate(conv.last_message_at)}
                        </p>
                        {conv.unread_count > 0 && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-500 text-white text-xs rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className={`${textSecondary} text-sm mt-2 truncate`}>
                      {conv.last_message || 'Sin mensajes'}
                    </p>
                    {conv.last_message_from_patient === false && (
                      <p className={`text-xs mt-1 ${['delivered', 'read'].includes(conv.last_message_delivery_status) ? textMuted : 'text-amber-500'}`}>
                        {deliveryLabel(conv.last_message_delivery_status)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages Panel */}
        <Card className={`${cardBg} lg:col-span-2 flex flex-col overflow-hidden h-[70vh] min-h-[400px] lg:h-auto lg:min-h-0`}>
          {selectedConversation ? (
            <>
              <CardHeader className={`border-b ${borderColor} shrink-0`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <CardTitle className={textPrimary}>
                        {selectedConversation.patient_name || 'Paciente'}
                      </CardTitle>
                      <p className={`${textSecondary} text-sm`}>{selectedConversation.phone}</p>
                    </div>
                  </div>
                  {/* Takeover Controls */}
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" aria-label="Actualizar mensajes"
                      onClick={() => fetchMessages(selectedConversation.conversation_id)}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={toggleTakeover}
                      variant={isPaused ? "default" : "outline"}
                      size="sm"
                      className={isPaused ? "bg-orange-500 hover:bg-orange-600" : ""}
                      data-testid="btn-toggle-takeover"
                    >
                      {isPaused ? (
                        <>
                          <Play className="w-4 h-4 mr-1" /> Reactivar Bot
                        </>
                      ) : (
                        <>
                          <Pause className="w-4 h-4 mr-1" /> Pausar Bot
                        </>
                      )}
                    </Button>
                    {isPaused && (
                      <span className="text-xs px-2 py-1 bg-orange-500/20 text-orange-500 rounded-full">
                        Modo Manual
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              {messageError && <p role="alert" className="p-3 text-red-500">{messageError}</p>}
              <CardContent className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                {hasMoreMessages && (
                  <Button variant="outline" size="sm" disabled={loadingOlder}
                    onClick={() => fetchMessages(selectedConversation.conversation_id, { older: true })}>
                    {loadingOlder ? 'Cargando anteriores...' : 'Cargar mensajes anteriores'}
                  </Button>
                )}
                {loadingMessages ? (
                  <div className={`text-center ${textSecondary} py-8`}>Cargando mensajes...</div>
                ) : messages.length === 0 ? (
                  <div className={`text-center ${textSecondary} py-8`}>No hay mensajes</div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.is_from_patient ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          msg.is_from_patient
                            ? messageBgPatient
                            : msg.is_ai_response
                            ? 'bg-emerald-600/30 text-emerald-100'
                            : 'bg-blue-600/30 text-blue-100'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${textMuted}`}>
                          {formatDate(msg.created_at)}
                          {msg.is_ai_response && ' • AI'}
                          {msg.manual_intervention && ' • Manual'}
                          {!msg.is_from_patient && msg.delivery_status && (
                            <span className={['failed', 'unknown'].includes(msg.delivery_status) ? 'text-red-300 font-medium' : ''}>
                              {' • '}{deliveryLabel(msg.delivery_status)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
              {/* Manual Message Input */}
              <div className={`p-4 border-t ${borderColor} shrink-0`}>
                {isPaused ? (
                  <div className="flex gap-2">
                    <Input
                      value={manualMessage}
                      onChange={(e) => setManualMessage(e.target.value)}
                      placeholder="Escribe un mensaje manual..."
                      className={`${inputBg} flex-1`}
                      onKeyPress={(e) => e.key === 'Enter' && sendManualMessage()}
                      data-testid="input-manual-message"
                    />
                    <Button 
                      onClick={sendManualMessage} 
                      disabled={sendingMessage || !manualMessage.trim()}
                      data-testid="btn-send-manual"
                    >
                      {sendingMessage ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : (
                  <p className={`${textMuted} text-sm text-center`}>
                    💡 Haz clic en "Pausar Bot" para responder manualmente sin que Gina intervenga.
                  </p>
                )}
              </div>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ChevronRight className={`w-12 h-12 ${textMuted} mx-auto mb-4`} />
                <p className={textSecondary}>Selecciona una conversación</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Conversations;


